<template>
    <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
            <div class="card-icon icon-style">
                <md-icon>attach_money</md-icon>
                {{ title }}
            </div>
        </md-card-header>
        <div class="has-invoice" v-if="hasInvoice">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Charges cannot be changed because job has been invoiced to a customer</i>
        </div>
        <div class="is-approved" v-if="isChargesApproved">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Charges cannot be changed because job has been approved</i>
        </div>
        <md-card-content>
            <div v-if="updatingRates">
                <loading-component-spinner loading-text="Rates Updating..." />
            </div>
            <p v-if="combineChargeLines.length === 0 && !updatingRates" class="grayedout-text text-center">
                No {{ title }} found
            </p>
            <div v-if="!updatingRates && combineChargeLines.length > 0">
                <md-table>
                    <md-table-row slot="header">
                        <md-table-head class="drag-icon"></md-table-head>
                        <md-table-head>Description</md-table-head>
                        <md-table-head>Unit</md-table-head>
                        <md-table-head>Amount</md-table-head>
                        <md-table-head v-if="$root.isDesktop && !isReadOnlyUser">
                            Actions
                        </md-table-head>
                    </md-table-row>

                    <div v-if="updatingRates">
                        <loading-component-spinner loading-text="Rates Updating..." />
                    </div>
                    <md-table-row
                        v-if="!updatingRates" v-for="(rate, index) in combineChargeLines"
                        :key="rate.id"
                        :class="{
                            'editable-input': editingIndex === index,
                            'readonly-row': editingLoadingIndex === index
                        }"
                    >
                        <rate-charge-rule
                            :rule-data="getChargeRuleData()"
                            :currency="getCurrency"
                            :rate="rate"
                            :rate-index="index"
                        />
                        <md-table-cell class="drag-icon">
                            <md-icon v-if="rate.isManualCharge || rate.manuallyChanged">
                                <span class="">
                                    drive_file_rename_outline
                                </span>
                            </md-icon>
                        </md-table-cell>
                        <md-table-cell class="description-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="editBuffer.description"
                                        @input="onEditChange"
                                        type="text"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.description }}
                            </template>
                        </md-table-cell>
                        <md-table-cell v-if="rate.type === 'hourly'" class="unitcharge-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.timeInput"
                                        @input="onEditChange"
                                        type="text"
                                        :class="[
                                            'md-input editable-value',
                                            {
                                                'invalid-time-format': editingIndex !== null && combineChargeLines[editingIndex].type === 'hourly'
                                                    && originalEditBuffer.timeInput && !$_validateHourlyTimeFormat(originalEditBuffer.timeInput)
                                            }
                                        ]"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                               {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell v-else :class="['unitcharge-container', { 'readonly-fuel-levy': rate.type === 'fuelLevy' || rate.type === 'surcharge' || rate.type === 'commission' }]">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.chargeUnit"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="amount-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model.number="editBuffer.amount"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{
                                    $_getRateAmount(rate)
                                        | currency(getCurrency)
                                }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="action-buttons">
                            <template v-if="editingIndex === index">
                                <template v-if="editingLoadingIndex === index">
                                    <md-progress-spinner md-mode="indeterminate" :md-diameter="23" />
                                </template>
                                <template v-else>
                                    <md-button
                                        :disabled="disableSaveButton()"
                                        class="md-success md-just-icon md-round"
                                        @click="saveEdit"
                                    >
                                        <md-icon>save</md-icon>
                                    </md-button>
                                    <md-button class="md-danger md-just-icon md-round" @click="cancelEdit">
                                        <md-icon>cancel</md-icon>
                                    </md-button>
                                </template>
                            </template>
                            <template v-else>
                                <md-button
                                    v-if="
                                        (
                                            (!isChargesApproved && !hasInvoice && !isReadOnlyUser && !rate.isService) 
                                            || (hasInvoice && invoiceStatus() === invoiceStatusAddNewLines && rate.type === 'draftCharge')
                                        )"
                                    title="Edit"
                                    class="md-success md-just-icon md-round"
                                    @click.stop="startEdit(index, rate)"
                                >
                                    <md-icon>edit</md-icon>
                                </md-button>
                                <md-button
                                    v-if="
                                        (
                                            (!isChargesApproved && !hasInvoice && !isReadOnlyUser && !rate.isService)
                                            || (hasInvoice && invoiceStatus() === invoiceStatusAddNewLines && rate.type === 'draftCharge')
                                        )"
                                    title="Remove"
                                    class="md-danger md-just-icon md-round"
                                    @click.stop="deleteChargeLine(rate)"
                                >
                                    <md-icon>delete</md-icon>
                                </md-button>
                            </template>
                        </md-table-cell>
                    </md-table-row>

                    <md-table-row v-if="!updatingRates">
                        <md-table-cell colspan="3" />
                        <md-table-cell class="total-style">
                            {{
                                $_getTotalAmount(combineChargeLines)
                                    | currency(getCurrency)
                            }}
                        </md-table-cell>
                        <md-table-cell />
                    </md-table-row>
                </md-table>
            </div>
        </md-card-content>
        <md-card-actions
            v-if="!isChargesApproved && !hasInvoice && !isReadOnlyUser"
            :class="{ 'is-loading-rates': updatingRates }"
        >
            <md-button
                v-if="!isReadOnlyUser"
                class="md-warning revert-button"
                @click="validateRevertingCharges()"
            >
                Revert to Default Charges
            </md-button>
            <md-button v-if="!isReadOnlyUser" class="md-success" @click="handleAddNewCharge()">
                Add Charge
            </md-button>
        </md-card-actions>
        <md-card-actions v-else-if="invoiceStatus() === invoiceStatusAddNewLines">
            <add-new-line-to-draft-invoice
                class="add-line"
                :invoice-id="getInvoiceId()"
                title="Charge"
                :stop-id="stopDetails ? stopDetails.stopId : null"
                :shipment-id="shipmentDetails ? shipmentDetails.shipmentId : null"
                :run-id="runDetails ? runDetails.runId : null"
                @lineCreated="addNewChargeLine"
            />
        </md-card-actions>
    </md-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { FeatureManager } from '@/directives';
import { FEATURE_NAMES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import { AddNewLineToDraftInvoice } from '@/components';
import ChargeItemModal from './ChargeItemModal';
import LoadingComponentSpinner from './LoadingComponentSpinner';
import RateChargeRule from './RateChargeRule';

export default {
    name: 'ChargeRatesList',
    components: {
        LoadingComponentSpinner,
        AddNewLineToDraftInvoice,
        RateChargeRule
    },
    mixins: [GeneralMixin, RatesEngineMixin, FeatureManager],
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            isIndividualUser: 'user/isIndividualUser',
            user: 'user/user'
        }),
        combineChargeLines() {
            const filteredServiceLines = this.serviceLines.filter(
                (s) => s.unitPriceExTax != null && s.unitPriceExTax !== 0 && s.quantity != null
            );
            return this.chargeLines.concat(filteredServiceLines);
        },
        getCurrency() {
            return this.shipmentDetails?.currency ?? this.stopDetails?.currency ?? this.orderDetails?.currency ?? this.runDetails?.currency; 
        }
    },
    props: {
        chargeLines: {
            type: Array,
            default: () => []
        },
        shipmentDetails: {
            type: Object,
            default: () => null
        },
        stopDetails: {
            type: Object,
            default: () => null
        },
        title: {
            type: String,
            default: null
        },
        hasInvoice: {
            type: Boolean,
            default: false
        },
        ratesHaveChanged: {
            type: Boolean,
            default: false
        },
        serviceLines: {
            type: Array,
            default: () => []
        },
        isOnlyService: {
            type: Boolean,
            default: false
        },
        orderDetails:{
            type: Object,
            default: () => null
        },
        runDetails: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            updatingRates: false,
            isChargesApproved: false,
            invoiceStatusAddNewLines: 'Draft',
            editingIndex: null,
            editingLoadingIndex: null,
            editBuffer: {
                description: '',
                amount: 0,
                chargeUnit: null,
                timeInput: null
            },
            originalEditBuffer: {
                description: '',
                amount: 0,
                chargeUnit: 0,
                timeInput: null
            },
            showEditActions: false,
            originalChargeUnit: null,
            trimmedUnit: null,
            validationTimeout: null
        };
    },
    watch: {
        ratesHaveChanged(newValue, oldValue) {
            this.updatingRates = newValue;
        }
    },
    mounted() {
        if (this.$_hasFeature(FEATURE_NAMES.JobsChargeApproval)) {
            this.isChargesApproved = this.shipmentDetails?.isChargesApproved ?? this.stopDetails?.isChargesApproved ?? this.orderDetails?.isChargesApproved ?? this.runDetails?.isChargesApproved ?? false;
        }
    },
    beforeDestroy() {
        // Clean up validation timeout when component is destroyed
        if (this.validationTimeout) {
            clearTimeout(this.validationTimeout);
            this.validationTimeout = null;
        }
    },
    methods: {
        deleteChargeLine(charge) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Delete Charge',
                    body: 'Are you sure you want to delete this charge?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isListLoading = true;
                        await this.handleDeleteCharge(charge.id);
                    }
                });
        },
        async handleDeleteCharge(id) {
            const payload = {
                method: 'delete'
            };
            const api = `/api/rates/charge/delete/${id}`;
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Charge was deleted!',
                    type: 'success'
                });
            } catch (e) {
                const message = 'Error in deleting a charge.';
                showErrorMessage(this, message, e);
            } finally {
                this.emitUpdate();
            }
        },
        validateRevertingCharges() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Revert Charges',
                    body: 'Are you sure you want to revert charges? <br /><br /> This cannot be undone.',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.handleRevertToDefaultCharges(true, false);
                    }
                });
        },
        async handleRevertToDefaultCharges() {
            try {
                this.$_handleLoaderState(true, 'REVERTING...');

                const data = {
                    isCosting: false,
                    isCharging: true,
                    revertCosts: false,
                    revertCharges: true
                };

                const payload = {
                    method: 'post',
                    data
                };

                const endpoint = this.getRatesEndpoint();

                if (endpoint === null) {
                    this.$notify({
                        message: 'Cannot revert rates',
                        type: 'danger'
                    });
                    return;
                }

                await handleRequests(endpoint, payload);

                this.$notify({
                    message: 'Charges Reverted',
                    type: 'success'
                });

            } catch (error) {
                const message = 'Error Reverting Charges';
                showErrorMessage(this, message, null);
            } finally {
                this.postSave();
                this.ratesIsLoading = false;
                this.$_handleLoaderState(false);
                this.emitUpdate();
            }
        },
        getRatesEndpoint() {
            if (this.shipmentDetails) {
                return `/api/rates/shipment/${this.shipmentDetails.shipmentId}/generate-rates`;
            }
            
            if (this.stopDetails) {
                return `/api/rates/stop/${this.stopDetails.stopId}/generate-rates`;
            }
            
            if (this.orderDetails) {
                return `/api/rates/order/${this.orderDetails.orderId}/generate-rates`;
            }

            if (this.runDetails) {
                return `/api/rates/run/${this.runDetails.runId}/generate-rates`;
            }

            return null;
        },
        handleAddNewCharge() {
            this.$modal
                .show(ChargeItemModal, {
                    shipmentId: this.shipmentDetails?.shipmentId,
                    stopId: this.stopDetails?.stopId,
                    orderId: this.orderDetails?.orderId,
                    runId: this.runDetails?.runId,
                    isManualCharge: true,
                    manuallyChanged: false,
                    chargeLine: {
                        description: null,
                        amount: 0,
                        chargeUnit: null,
                        quantity: 1
                    }
                })
                .then((response) => {
                    this.emitUpdate();
                    this.$modal.hide();
                });

            this.postSave();
        },
        emitUpdate() {
            if (this.shipmentDetails?.shipmentId) {
                this.$emit('updateShipmentDetails');
            }

            if (this.stopDetails?.stopId) {
                this.$emit('updateStopDetails');
            }

            if (this.orderDetails?.orderId) {
                this.$emit('updateOrderDetails');
            }

            if (this.runDetails?.runId) {
                this.$emit('updateRunDetails');
            }
        },
        getInvoiceId() {
            if (this.stopDetails && this.stopDetails.customerInvoiceId != null) {
                return this.stopDetails.customerInvoiceId;
            }

            if (this.shipmentDetails && this.shipmentDetails.customerInvoiceId != null) {
                return this.shipmentDetails.customerInvoiceId;
            }

            if (this.orderDetails && this.orderDetails.customerInvoiceId != null) {
                return this.orderDetails.customerInvoiceId;
            }

            if (this.runDetails && this.runDetails.customerInvoiceId != null) {
                return this.runDetails.customerInvoiceId;
            }

            return null;
        },
        invoiceStatus() {
            if (this.stopDetails && this.stopDetails.customerInvoiceStatus != null) {
                return this.stopDetails.customerInvoiceStatus;
            }

            if (this.shipmentDetails && this.shipmentDetails.customerInvoiceStatus != null) {
                return this.shipmentDetails.customerInvoiceStatus;
            }

            if (this.orderDetails && this.orderDetails.customerInvoiceStatus != null) {
                return this.orderDetails.customerInvoiceStatus;
            }

            if (this.runDetails && this.runDetails.customerInvoiceStatus != null) {
                return this.runDetails.customerInvoiceStatus;
            }

            return null;
        },
        addNewChargeLine(chargeLine) {
            const mappedChargeLine = {
                id: chargeLine.id,
                description: chargeLine.description,
                quantity: costLine.quantity,
                unitPriceExTax: chargeLine.unitPriceExTax,
                isManualCharge: chargeLine.isManualCharge,
                manuallyChanged: true,
                type: chargeLine.type
            };

            this.costLines.push(mappedChargeLine);
            this.postSave();
        },
        getChargeRuleData() {
            const rule = this.shipmentDetails?.shipmentAppliedChargingRule ?? this.stopDetails?.stopAppliedChargingRule ?? this.orderDetails?.orderAppliedChargingRule ??
            this.runDetails?.runAppliedChargingRule ?? null;

            if (rule == null || rule.ruleType == null)
                return null;

            return rule;
        },
        startEdit(index, rate) {
            this.$_startEdit(index, rate, this.editBuffer, this.originalEditBuffer);
        },
        onEditChange() {
            this.showEditActions = true;
            
            // Clear any existing timeout
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
            }
            
            // Validate hourly time format if editing an hourly rate with debounce
            if (this.editingIndex !== null) {
                const rate = this.combineChargeLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    this.validationTimeout = setTimeout(() => {
                        const isValid = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                        if (!isValid) {
                            this.$notify({
                                message: 'Invalid time format. Please use format like "4h 54m" or "2h 30m"',
                                type: 'warning'
                            });
                        }
                    }, 1000);
                }
            }
        },
        disableSaveButton() {
            if (!this.showEditActions) {
                return false;
            }
            
            // Additional validation for hourly rates
            if (this.editingIndex !== null) {
                const rate = this.combineChargeLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    const canSave = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                    return !canSave;
                }
            }
            
            return false;
        },
        postSave() {
            this.editingIndex = null;
            this.editingLoadingIndex = null;
            this.showEditActions = false;
        },
        async saveEdit() {
            let entityType;
            let entityId;
            
            if (this.shipmentDetails !== null) {
                entityType = 'Shipment';
                entityId = this.shipmentDetails.shipmentId;
            } else if (this.orderDetails !== null) {
                entityType = 'Order';
                entityId = this.orderDetails.orderId;
            } else if (this.stopDetails !== null) {
                entityType = 'Stop';
                entityId = this.stopDetails.stopId;
            } else if (this.runDetails !== null) {
                entityType = 'Run';
                entityId = this.runDetails.runId;
            }
            
            const result = this.$_saveEdit(
                this.combineChargeLines,
                this.editBuffer,
                this.originalEditBuffer,
                entityType,
                entityId,
                'Charge',
                this.isManualCharge
            );

            try {
                if (result && result.api) {
                    await handleRequests(result.api, result.payload);
                    this.$notify({
                        message: `${result.entityType} updated!`,
                        type: 'success'
                    });
                }
            } catch (e) {
                const message = `Cannot edit ${result.entityType.toLowerCase()}.`;
                showErrorMessage(this, message, e);
            } finally {
                this.postSave();
                this.emitUpdate();
            }
        },
        cancelEdit() {
            // Clear validation timeout when cancelling edit
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
                this.validationTimeout = null;
            }
            
            this.editingIndex = null;
            this.showEditActions = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.total-style {
    color: #4caf50 !important;
    font-weight: bold !important;
}

.icon-style {
    color: white;
}

.revert-button {
    margin-inline-end: auto !important;
}

.disabled-rates {
    pointer-events: none;
}

.is-loading-rates {
    margin-top: 150px !important;
}

.action-buttons {
    button,
    ::v-deep button {
        margin: 0 2px;
    }

    button:last-child {
        margin-right: 0;
    }
}

::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

.has-invoice,
.is-approved {
    padding-top: 15px;
    padding-left: 20px;
}

.invoice-icon-color {
    color: #4caf50 !important;
}

.add-line {
    pointer-events: auto !important;
}

.edit-actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
}

.md-table-cell .md-field {
  margin: 0;
  padding: 0;
}

.md-table-cell .md-input,
.md-table-cell input {
    width: 30px;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
    margin: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    min-width: 0;
    height: 25px;
    box-sizing: border-box;
}

.editable-input {
  background: #f0f0f0 !important;
  border-radius: 4px;
  padding: 10px 15px;
  transition: border 0.2s;
}

.editable-input:focus {
  outline: none;
}

.editable-value {
    border-bottom: 2px solid #e0e0e0 !important;
}

.amount-container {
    width: 150px !important;
}

.description-container {
    width: 300px !important;
}

.row-updating {
  background: #f0f0f0 !important;
}

.edit-spinner {
  display: inline-block;
  vertical-align: middle;
  margin: 0 8px;
}

.unitcharge-container {
    width: 150px;
}

.readonly-row {
    pointer-events: none;
    opacity: 0.6; 
}

.readonly-fuel-levy {
    pointer-events: none;
    opacity: 0.8; 
}

.invalid-time-format {
    background-color: #ffebee !important;
}
</style>
